import type { SemanticSection } from '../track/index.ts';
import {
  add,
  clamp,
  cross,
  dot,
  finite,
  length,
  lerp,
  normalize,
  scale,
  sub,
  vec,
} from '../track/index.ts';
import {
  FIXED_PHYSICS_STEP,
  FORCE_WINDOWS_MS,
  STANDARD_GRAVITY,
  type CarPose,
  type CoasterFamilyDefinition,
  type Direction,
  type DynamicsBackend,
  type ForceSample,
  type GVector,
  type SimulationConfig,
  type SimulationFrame,
  type TrackBoundState,
  type ValidationIssue,
  type ValidationSeverity,
} from './types.ts';

interface ForceHistoryPoint extends GVector {
  readonly time: number;
  readonly carIndex: number;
}
interface StationRuntime {
  readonly sectionId: string;
  readonly enteredAt: number;
  readonly released: boolean;
}
interface Derivative {
  readonly ds: number;
  readonly dv: number;
}

const DEFAULT_GRAVITY = vec(0, -STANDARD_GRAVITY, 0);
const VELOCITY_EPSILON = 1e-7;

function numericParameter(section: SemanticSection, key: string, fallback: number): number {
  const value = section.parameters?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function booleanParameter(section: SemanticSection, key: string, fallback: boolean): boolean {
  const value = section.parameters?.[key];
  return typeof value === 'boolean' ? value : fallback;
}

function smoothstep(value: number): number {
  const t = clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
}

function sectionBlend(
  section: SemanticSection,
  distanceValue: number,
  trackLength: number,
  closed: boolean,
): number {
  const easeDistance = Math.max(0, numericParameter(section, 'easeDistance', 1));
  if (easeDistance === 0) return 1;
  let fromStart: number;
  let toEnd: number;
  if (section.startDistance <= section.endDistance) {
    fromStart = distanceValue - section.startDistance;
    toEnd = section.endDistance - distanceValue;
  } else if (closed) {
    fromStart = (distanceValue - section.startDistance + trackLength) % trackLength;
    toEnd = (section.endDistance - distanceValue + trackLength) % trackLength;
  } else {
    return 0;
  }
  return smoothstep(fromStart / easeDistance) * smoothstep(toEnd / easeDistance);
}

function sectionAcceleration(
  section: SemanticSection | null,
  velocity: number,
  distanceValue: number,
  trackLength: number,
  closed: boolean,
): number {
  if (!section) return 0;
  const blend = sectionBlend(section, distanceValue, trackLength, closed);
  switch (section.type) {
    case 'lift': {
      const target = Math.max(0, numericParameter(section, 'targetSpeed', 4.5));
      const maximum = Math.max(0, numericParameter(section, 'maxAcceleration', 3));
      const gain = Math.max(0, numericParameter(section, 'gain', 2.5));
      if (velocity < 0 && booleanParameter(section, 'antiRollback', true)) return maximum;
      return clamp((target - velocity) * gain, 0, maximum) * blend;
    }
    case 'launch': {
      const target = Math.max(0, numericParameter(section, 'targetSpeed', 20));
      const maximum = Math.max(0, numericParameter(section, 'maxAcceleration', 10));
      const gain = Math.max(0, numericParameter(section, 'gain', 3));
      return clamp((target - velocity) * gain, 0, maximum) * blend;
    }
    case 'station': {
      const target = Math.max(0, numericParameter(section, 'targetSpeed', 0));
      const maximumAcceleration = Math.max(0, numericParameter(section, 'maxAcceleration', 2.5));
      const maximumDeceleration = Math.max(0, numericParameter(section, 'maxDeceleration', 4));
      const gain = Math.max(0, numericParameter(section, 'gain', 4));
      // A returning train can settle exactly on the station entry boundary,
      // where the generic operational easing reaches zero. Retain a small
      // station drive there so the train advances to its front-aligned parking
      // point instead of being stranded as soon as the lead car enters.
      const stationBlend = Math.max(0.2, blend);
      if (velocity > target)
        return -Math.min(maximumDeceleration, (velocity - target) * gain) * stationBlend;
      if (velocity < target)
        return Math.min(maximumAcceleration, (target - velocity) * gain) * stationBlend;
      return 0;
    }
    case 'brake': {
      const target = Math.max(0, numericParameter(section, 'targetSpeed', 5));
      const maximum = Math.max(0, numericParameter(section, 'maxDeceleration', 8));
      const gain = Math.max(0, numericParameter(section, 'gain', 4));
      if (velocity > target) return -Math.min(maximum, (velocity - target) * gain) * blend;
      if (velocity < -target) return Math.min(maximum, (-target - velocity) * gain) * blend;
      return 0;
    }
    default:
      return 0;
  }
}

function signedVelocity(state: TrackBoundState): number {
  return state.velocity * state.direction;
}

function carDistance(
  referenceDistance: number,
  carIndex: number,
  family: CoasterFamilyDefinition,
): number {
  return referenceDistance - carIndex * family.train.carSpacing;
}

function validateConfig(
  state: TrackBoundState,
  config: SimulationConfig,
  dt: number,
): ValidationIssue | null {
  const train = config.family.train;
  if (
    ![state.sRef, state.velocity, state.time, dt].every(Number.isFinite) ||
    state.velocity < 0 ||
    (state.direction !== 1 && state.direction !== -1)
  ) {
    return { code: 'invalid_state', severity: 'block', messageKey: 'physics.invalid_state' };
  }
  if (!(dt > 0) || dt > 0.05)
    return {
      code: 'invalid_timestep',
      severity: 'block',
      messageKey: 'physics.invalid_timestep',
      measured: dt,
    };
  if (
    !config.sampler.valid ||
    !(config.sampler.length > 0) ||
    !Number.isFinite(config.sampler.length)
  ) {
    return {
      code: 'invalid_track_sampler',
      severity: 'block',
      messageKey: 'physics.invalid_track_sampler',
    };
  }
  if (
    !Number.isInteger(train.carCount) ||
    train.carCount < 1 ||
    train.carCount > (config.family.limits.maxCars ?? 64) ||
    ![
      train.carMass,
      train.carSpacing,
      train.bogieSpacing,
      train.rollingResistanceCoefficient,
      train.dragCoefficient,
      train.dragArea,
    ].every(Number.isFinite) ||
    train.carMass <= 0 ||
    train.carSpacing < 0 ||
    train.bogieSpacing < 0 ||
    train.rollingResistanceCoefficient < 0 ||
    train.dragCoefficient < 0 ||
    train.dragArea < 0
  ) {
    return { code: 'invalid_train', severity: 'block', messageKey: 'physics.invalid_train' };
  }
  return null;
}

function generalizedAcceleration(
  referenceDistance: number,
  velocity: number,
  config: SimulationConfig,
): number {
  const train = config.family.train;
  const gravity = config.gravity ?? DEFAULT_GRAVITY;
  let force = 0;
  for (let carIndex = 0; carIndex < train.carCount; carIndex += 1) {
    const sample = config.sampler.sampleByDistance(
      carDistance(referenceDistance, carIndex, config.family),
    );
    force += train.carMass * dot(gravity, sample.tangent);
    force +=
      train.carMass *
      sectionAcceleration(
        sample.section,
        velocity,
        sample.distance,
        config.sampler.length,
        config.sampler.closed,
      );
  }
  const totalMass = train.carMass * train.carCount;
  if (Math.abs(velocity) > VELOCITY_EPSILON) {
    force -= Math.sign(velocity) * train.rollingResistanceCoefficient * totalMass * length(gravity);
    force -=
      0.5 *
      (config.airDensity ?? 1.225) *
      train.dragCoefficient *
      train.dragArea *
      velocity *
      Math.abs(velocity);
  }
  const acceleration = force / totalMass;
  return Number.isFinite(acceleration) ? acceleration : 0;
}

function derivative(
  referenceDistance: number,
  velocity: number,
  config: SimulationConfig,
): Derivative {
  return { ds: velocity, dv: generalizedAcceleration(referenceDistance, velocity, config) };
}

function rk4(
  referenceDistance: number,
  velocity: number,
  config: SimulationConfig,
  dt: number,
): { s: number; v: number } {
  const first = derivative(referenceDistance, velocity, config);
  const second = derivative(
    referenceDistance + first.ds * dt * 0.5,
    velocity + first.dv * dt * 0.5,
    config,
  );
  const third = derivative(
    referenceDistance + second.ds * dt * 0.5,
    velocity + second.dv * dt * 0.5,
    config,
  );
  const fourth = derivative(referenceDistance + third.ds * dt, velocity + third.dv * dt, config);
  return {
    s: referenceDistance + (dt * (first.ds + 2 * second.ds + 2 * third.ds + fourth.ds)) / 6,
    v: velocity + (dt * (first.dv + 2 * second.dv + 2 * third.dv + fourth.dv)) / 6,
  };
}

function normalizeCoordinate(
  distanceValue: number,
  config: SimulationConfig,
): { distance: number; boundary: boolean } {
  const { sampler } = config;
  if (sampler.closed)
    return {
      distance: ((distanceValue % sampler.length) + sampler.length) % sampler.length,
      boundary: false,
    };
  return {
    distance: clamp(distanceValue, 0, sampler.length),
    boundary: distanceValue < 0 || distanceValue > sampler.length,
  };
}

function activeSections(referenceDistance: number, config: SimulationConfig): SemanticSection[] {
  const result: SemanticSection[] = [];
  for (let carIndex = 0; carIndex < config.family.train.carCount; carIndex += 1) {
    const section = config.sampler.sampleByDistance(
      carDistance(referenceDistance, carIndex, config.family),
    ).section;
    if (section) result.push(section);
  }
  return result;
}

function clampOperationalVelocity(
  previous: number,
  next: number,
  sections: readonly SemanticSection[],
): number {
  let value = next;
  for (const section of sections) {
    if (section.type === 'lift' && booleanParameter(section, 'antiRollback', true) && value < 0)
      value = 0;
    if (section.type === 'launch') {
      const target = Math.max(0, numericParameter(section, 'targetSpeed', 20));
      if (previous <= target && value > target) value = target;
    }
    if (section.type === 'brake' || section.type === 'station') {
      const target = Math.max(
        0,
        numericParameter(section, 'targetSpeed', section.type === 'station' ? 0 : 5),
      );
      if (previous > target && value < target) value = target;
      if (previous < -target && value > -target) value = -target;
      if (target === 0 && previous * value < 0) value = 0;
      if (section.type === 'station' && target > 0 && value < 0) value = 0;
    }
  }
  return value;
}

function poseForCar(
  referenceDistance: number,
  carIndex: number,
  config: SimulationConfig,
): CarPose {
  const train = config.family.train;
  const centerDistance = carDistance(referenceDistance, carIndex, config.family);
  const halfBogie = train.bogieSpacing * 0.5;
  const front = config.sampler.sampleByDistance(centerDistance + halfBogie);
  const rear = config.sampler.sampleByDistance(centerDistance - halfBogie);
  const center = config.sampler.sampleByDistance(centerDistance);
  let forward = normalize(sub(front.position, rear.position));
  if (length(forward) < 0.5) forward = center.tangent;
  const up = normalize(center.up);
  const right = normalize(cross(up, forward));
  return {
    carIndex,
    distance: center.distance,
    position: lerp(front.position, rear.position, 0.5),
    forward,
    up: normalize(cross(forward, right)),
    right,
    frontBogieDistance: front.distance,
    rearBogieDistance: rear.distance,
  };
}

function severityFor(vector: GVector): ValidationSeverity {
  const lateral = Math.abs(vector.lateral);
  const longitudinal = Math.abs(vector.longitudinal);
  if (vector.vertical > 8 || vector.vertical < -3 || lateral > 4 || longitudinal > 4)
    return 'block';
  if (vector.vertical > 6 || vector.vertical < -1.5 || lateral > 2 || longitudinal > 2)
    return 'severe';
  if (vector.vertical > 5 || vector.vertical < -1 || lateral > 1.25 || longitudinal > 1.25)
    return 'warning';
  if (vector.vertical > 4 || vector.vertical < -0.5 || lateral > 0.75 || longitudinal > 0.75)
    return 'info';
  return 'info';
}

function parseHistory(runtime: Readonly<Record<string, unknown>>): ForceHistoryPoint[] {
  const candidate = runtime.forceHistory;
  if (!Array.isArray(candidate)) return [];
  return candidate.filter((item): item is ForceHistoryPoint => {
    if (!item || typeof item !== 'object') return false;
    const point = item as Partial<ForceHistoryPoint>;
    return [point.time, point.carIndex, point.vertical, point.lateral, point.longitudinal].every(
      Number.isFinite,
    );
  });
}

function parseStationRuntime(runtime: Readonly<Record<string, unknown>>): StationRuntime | null {
  const candidate = runtime.station;
  if (!candidate || typeof candidate !== 'object') return null;
  const station = candidate as Partial<StationRuntime>;
  if (
    typeof station.sectionId !== 'string' ||
    !Number.isFinite(station.enteredAt) ||
    typeof station.released !== 'boolean'
  )
    return null;
  return station as StationRuntime;
}

function averageWindow(
  history: readonly ForceHistoryPoint[],
  carIndex: number,
  now: number,
  durationMs: number,
): GVector {
  const minimumTime = now - durationMs / 1000;
  const points = history.filter(
    (point) => point.carIndex === carIndex && point.time >= minimumTime && point.time <= now + 1e-9,
  );
  if (!points.length) return { vertical: 0, lateral: 0, longitudinal: 0 };
  const total = points.reduce(
    (sum, point) => ({
      vertical: sum.vertical + point.vertical,
      lateral: sum.lateral + point.lateral,
      longitudinal: sum.longitudinal + point.longitudinal,
    }),
    { vertical: 0, lateral: 0, longitudinal: 0 },
  );
  return {
    vertical: total.vertical / points.length,
    lateral: total.lateral / points.length,
    longitudinal: total.longitudinal / points.length,
  };
}

function forceForCar(
  referenceDistance: number,
  velocity: number,
  acceleration: number,
  carIndex: number,
  config: SimulationConfig,
  history: ForceHistoryPoint[],
  time: number,
  dt: number,
): ForceSample {
  const sample = config.sampler.sampleByDistance(
    carDistance(referenceDistance, carIndex, config.family),
  );
  const gravity = config.gravity ?? DEFAULT_GRAVITY;
  const worldAcceleration = add(
    scale(sample.tangent, acceleration),
    scale(sample.curvature, velocity * velocity),
  );
  const specificForce = scale(sub(worldAcceleration, gravity), 1 / STANDARD_GRAVITY);
  const raw = {
    vertical: dot(specificForce, sample.up),
    lateral: dot(specificForce, sample.right),
    longitudinal: dot(specificForce, sample.tangent),
  };
  const historyPoint: ForceHistoryPoint = { time, carIndex, ...raw };
  history.push(historyPoint);
  const windows: Record<string, GVector> = {};
  FORCE_WINDOWS_MS.forEach((windowMs) => {
    windows[String(windowMs)] = averageWindow(history, carIndex, time, windowMs);
  });
  return {
    carIndex,
    distance: sample.distance,
    direction: velocity < 0 ? -1 : 1,
    speed: Math.abs(velocity),
    durationMs: dt * 1000,
    severity: severityFor(windows['200']),
    ...raw,
    windows,
  };
}

function invalidFrame(state: TrackBoundState, issue: ValidationIssue): SimulationFrame {
  const safeState: TrackBoundState = {
    sRef: Number.isFinite(state.sRef) ? state.sRef : 0,
    velocity: 0,
    direction: state.direction === -1 ? -1 : 1,
    time: Number.isFinite(state.time) ? state.time : 0,
    sectionRuntime: {},
  };
  return { time: safeState.time, referenceState: safeState, cars: [], forces: [], issues: [issue] };
}

export class TrackBoundDynamics implements DynamicsBackend<TrackBoundState> {
  readonly motionModel = 'track_bound_v1';

  step(state: TrackBoundState, config: SimulationConfig, dt = FIXED_PHYSICS_STEP): SimulationFrame {
    const invalid = validateConfig(state, config, dt);
    if (invalid) return invalidFrame(state, invalid);
    try {
      const previousVelocity = signedVelocity(state);
      const referenceSection = config.sampler.sampleByDistance(state.sRef).section;
      const previousStationRuntime = parseStationRuntime(state.sectionRuntime);
      // Keep the dispatch state for the full circuit. Without this, returning to the
      // station looks like a fresh initial hold and can stop an overspeed train in one frame.
      let stationRuntime: StationRuntime | null = previousStationRuntime;
      let stationHeld = false;
      if (referenceSection?.type === 'station') {
        stationRuntime = previousStationRuntime
          ? { ...previousStationRuntime, sectionId: referenceSection.id }
          : { sectionId: referenceSection.id, enteredAt: state.time, released: false };
        const holdSeconds = Math.max(0, numericParameter(referenceSection, 'holdSeconds', 0));
        if (!stationRuntime.released && state.time + dt < stationRuntime.enteredAt + holdSeconds) {
          stationHeld = true;
        } else if (!stationRuntime.released) {
          stationRuntime = { ...stationRuntime, released: true };
        }
      }
      const integrated = stationHeld
        ? { s: state.sRef, v: 0 }
        : rk4(state.sRef, previousVelocity, config, dt);
      const normalized = normalizeCoordinate(integrated.s, config);
      const sections = activeSections(normalized.distance, config);
      const unsupportedSection = sections.find(
        (section) =>
          ['station', 'lift', 'launch', 'brake', 'photo'].includes(section.type) &&
          !config.family.capabilities.has(section.type),
      );
      if (unsupportedSection) {
        return invalidFrame(state, {
          code: 'unsupported_section',
          severity: 'block',
          messageKey: 'physics.unsupported_section',
          sectionId: unsupportedSection.id,
        });
      }
      let nextVelocity = clampOperationalVelocity(previousVelocity, integrated.v, sections);
      if (normalized.boundary) nextVelocity = 0;
      const liftRollbackBlocked =
        sections.some(
          (section) => section.type === 'lift' && booleanParameter(section, 'antiRollback', true),
        ) && nextVelocity <= 0;
      const nextDistance =
        liftRollbackBlocked && integrated.s < state.sRef ? state.sRef : normalized.distance;
      if (!Number.isFinite(nextDistance) || !Number.isFinite(nextVelocity)) {
        return invalidFrame(state, {
          code: 'solver_divergence',
          severity: 'block',
          messageKey: 'physics.solver_divergence',
        });
      }
      const direction: Direction =
        nextVelocity < -VELOCITY_EPSILON
          ? -1
          : nextVelocity > VELOCITY_EPSILON
            ? 1
            : state.direction;
      const time = state.time + dt;
      const acceleration = stationHeld
        ? 0
        : generalizedAcceleration(nextDistance, nextVelocity, config);
      const history = parseHistory(state.sectionRuntime).filter(
        (point) => point.time >= time - 2.01,
      );
      const forces = Array.from({ length: config.family.train.carCount }, (_, carIndex) =>
        forceForCar(nextDistance, nextVelocity, acceleration, carIndex, config, history, time, dt),
      );
      const nextState: TrackBoundState = {
        sRef: nextDistance,
        velocity: Math.abs(nextVelocity),
        direction,
        time,
        sectionRuntime: {
          forceHistory: history,
          ...(stationRuntime ? { station: stationRuntime } : {}),
        },
      };
      const cars = Array.from({ length: config.family.train.carCount }, (_, carIndex) =>
        poseForCar(nextDistance, carIndex, config),
      );
      const issues: ValidationIssue[] = [];
      const finiteForces = forces.every((force) =>
        [force.vertical, force.lateral, force.longitudinal, force.distance, force.speed].every(
          Number.isFinite,
        ),
      );
      const finiteCars = cars.every((car) =>
        [car.position, car.forward, car.up, car.right].every(finite),
      );
      if (!finiteForces || !finiteCars) {
        return invalidFrame(state, {
          code: 'non_finite_sample',
          severity: 'block',
          messageKey: 'physics.non_finite_sample',
        });
      }
      const speedLimit = config.family.limits.maxSpeed ?? 100;
      if (nextState.velocity > speedLimit)
        issues.push({
          code: 'speed_limit',
          severity: nextState.velocity > 100 ? 'block' : 'severe',
          messageKey: 'physics.speed_limit',
          measured: nextState.velocity,
          threshold: speedLimit,
        });
      return { time, referenceState: nextState, cars, forces, issues };
    } catch {
      return invalidFrame(state, {
        code: 'sampling_failure',
        severity: 'block',
        messageKey: 'physics.sampling_failure',
      });
    }
  }
}

export function createTrackBoundState(distance = 0, signedSpeed = 0): TrackBoundState {
  return {
    sRef: Number.isFinite(distance) ? distance : 0,
    velocity: Number.isFinite(signedSpeed) ? Math.abs(signedSpeed) : 0,
    direction: signedSpeed < 0 ? -1 : 1,
    time: 0,
    sectionRuntime: {},
  };
}

export function simulateSteps(
  backend: DynamicsBackend<TrackBoundState>,
  state: TrackBoundState,
  config: SimulationConfig,
  count: number,
  dt = FIXED_PHYSICS_STEP,
): SimulationFrame {
  let frame: SimulationFrame = {
    time: state.time,
    referenceState: state,
    cars: [],
    forces: [],
    issues: [],
  };
  for (let index = 0; index < count; index += 1)
    frame = backend.step(frame.referenceState, config, dt);
  return frame;
}
